import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { client, consoleLogInDev } from 'shared';
import { EmptyText, PillButton, Rating, Row, Select, Stack, Text, TextArea, Title } from 'library/ui';
import { PageStates } from 'constants/PageStates';
import { IProvider, IProviderReviewAnswer, ILoadResponse, BusinessLocation, isProgramImpactOption } from 'models';
import HighStarProviderReview from './HighStarProviderReview';
import LowStarProviderReview from './LowStarProviderReview';
import ProfileImage from './components/ProfileImage';
import ReviewOptionButton, { ReviewOptionTone } from './components/ReviewOptionButton';
import ProgramImpactSection from './components/ProgramImpactSection';
import { splitReviewOptions } from './reviewOptions';
import { MainContainer, TextContainer } from './components/Containers';
import useQueryParams from 'shared/QueryParamsHook';

interface ReviewProviderParams {
    id: string;
}

const ReviewProvider = ({
    promptedByActionType,
    userId,
}: {
    promptedByActionType?: 'block' | 'prefer';
    userId?: number;
}) => {
    const { id: uId } = useParams<ReviewProviderParams>();
    const query = useQueryParams();
    const [pageState, setPageState] = useState(PageStates.neutral);
    const [provider, setProvider] = useState<IProvider>();
    const [reviewOptions, setReviewOptions] = useState<IProviderReviewAnswer[]>([]);
    const [businessLocations, setBusinessLocations] = useState<BusinessLocation[]>([]);
    const [selectedCenter, setSelectedCenter] = useState<number>();
    const [canReview, setCanReview] = useState(true);
    const [alreadyReviewed, setAlreadyReviewed] = useState(false);
    const [totalScore, setTotalScore] = useState<number | undefined>(undefined);
    const [additionalFeedback, setAdditionalFeedback] = useState<string>();
    const [errorMessage, setErrorMessage] = useState('');

    const isPositive = (val: number | undefined) => val && val > 2;
    const isNegative = (val: number | undefined) => val && val <= 2;

    useEffect(() => {
        setPageState(PageStates.loading);
        client(`api/provider-review/get-review-info/?uid=${userId ?? uId}`)
            .then((response: ILoadResponse) => {
                setProvider(response.provider);
                setReviewOptions(response.options as IProviderReviewAnswer[]);
                setBusinessLocations(response.locations);
                setCanReview(response.can_review ?? true);
                if (response.locations?.length) {
                    setSelectedCenter(response.locations[0].id);
                }
                setAlreadyReviewed(!!response.completed && !promptedByActionType);
                setPageState(PageStates.neutral);
            })
            .catch((e) => {
                consoleLogInDev(e);
                setPageState(PageStates.neutral);
            });
        const initialValue = query.get('initialValue');
        if (initialValue && parseInt(initialValue)) {
            setTotalScore(parseInt(initialValue));
        }
    }, []);

    const submitReview = () => {
        setPageState(PageStates.submitting);
        setErrorMessage('');

        client('api/provider-review/', {
            body: {
                reviewee: provider?.id,
                total_score: totalScore,
                text: additionalFeedback,
                review_results: reviewOptions?.filter((x) => x.value)?.map((x) => ({ option: x.id })),
                business_location: selectedCenter,
            },
        })
            .then(() => {
                setPageState(PageStates.submitted);
            })
            .catch((error) => {
                setErrorMessage(error.message || 'There was an error submitting your review.');
                setPageState(PageStates.neutral);
            });
    };

    const onOptionChange = (id: number, value: boolean) => {
        setReviewOptions(
            reviewOptions.map((existingOption) =>
                existingOption.id === id ? { ...existingOption, value } : existingOption,
            ),
        );
    };

    const updateTotalScore = (newScore?: number) => {
        if (!newScore || !totalScore) {
            setReviewOptions(reviewOptions.map((x) => (isProgramImpactOption(x) ? x : { ...x, value: false })));
        }

        setTotalScore(newScore);
    };

    const setProgramImpact = (ids: number[]) => {
        setReviewOptions(
            reviewOptions.map((option) =>
                isProgramImpactOption(option) ? { ...option, value: ids.includes(option.id) } : option,
            ),
        );
    };

    const isButtonEnabled =
        pageState === PageStates.neutral &&
        totalScore &&
        reviewOptions.some((x) => x.value && !isProgramImpactOption(x));

    if (!provider) {
        return (
            <MainContainer>
                <EmptyText>Loading…</EmptyText>
            </MainContainer>
        );
    }

    if (!canReview || alreadyReviewed) {
        return (
            <MainContainer>
                <Title size="h2">
                    Review {provider.user.first_name} {provider.user.last_name}
                </Title>
                <TextContainer>
                    <Text align="center">
                        {alreadyReviewed
                            ? `Your center reviewed ${provider.user.first_name} in the last 7 days. You can leave another review once that week has passed.`
                            : 'You can only review workers who have worked a shift at your center in the last 14 days.'}
                    </Text>
                </TextContainer>
            </MainContainer>
        );
    }

    if (pageState === PageStates.submitted && isPositive(totalScore) && !promptedByActionType) {
        return <HighStarProviderReview provider={provider.user} />;
    }

    if (pageState === PageStates.submitted && isNegative(totalScore) && !promptedByActionType) {
        return <LowStarProviderReview provider={provider.user} />;
    }

    if (pageState === PageStates.submitted && promptedByActionType) {
        return (
            <MainContainer>
                <Title size="h2">Review Submitted!</Title>
                <TextContainer>
                    <Text align="center">
                        Thank you for taking the time to review{' '}
                        {provider?.user ? `${provider.user.first_name} ${provider.user.last_name}` : 'your provider'}.
                        We appreciate the feedback and will use this to better serve you and the rest of our users.
                    </Text>
                </TextContainer>
            </MainContainer>
        );
    }

    const { positive, negative, neutral, programImpact } = splitReviewOptions(reviewOptions);

    const optionSections: { title: string; options: IProviderReviewAnswer[]; tone: ReviewOptionTone }[] = [
        { title: 'What went well?', options: positive, tone: 'positive' },
        { title: 'What can this worker improve on?', options: negative, tone: 'negative' },
        {
            title: 'Which of the following can this worker do confidently and independently?',
            options: neutral,
            tone: 'neutral',
        },
    ];

    const initials = `${provider.user.first_name?.[0] ?? ''}${provider.user.last_name?.[0] ?? ''}`;

    return (
        <MainContainer>
            <Title size="h2">
                Review {provider.user.first_name} {provider.user.last_name}
            </Title>
            <ProfileImage
                image={provider.user.profile_picture?.image}
                imageId={(provider.id ?? -1).toString()}
                initials={initials}
            />

            {businessLocations.length > 1 && selectedCenter && (
                <Select
                    value={selectedCenter}
                    options={businessLocations.map((center) => ({ key: center.id, value: center.name }))}
                    onChange={(value) => setSelectedCenter(Number(value))}
                />
            )}

            <Text weight="bold" align="center">
                {promptedByActionType
                    ? `In order to ${promptedByActionType === 'prefer' ? 'prefer' : 'block'} ${provider.user.first_name}, please review them`
                    : `How was your shift with ${provider.user.first_name}?`}
            </Text>

            <Rating size="large" value={totalScore || 0} onChange={(value) => updateTotalScore(value ?? undefined)} />

            {totalScore ? (
                <Stack gap="md" align="center" fullWidth>
                    <Text weight="bold" align="center">
                        Please select at least one option below. (Select all that apply)
                    </Text>
                    {optionSections.map((section) => (
                        <Stack key={section.title} gap="sm" align="center" fullWidth>
                            <Text weight="bold" align="center">
                                {section.title}
                            </Text>
                            <Row gap="sm" justify="center" wrap>
                                {section.options.map((option) => (
                                    <ReviewOptionButton
                                        key={option.id}
                                        selected={!!option.value}
                                        tone={section.tone}
                                        onClick={() => onOptionChange(option.id, !option.value)}
                                    >
                                        {option.text}
                                    </ReviewOptionButton>
                                ))}
                            </Row>
                        </Stack>
                    ))}
                </Stack>
            ) : null}

            <Stack gap="sm" align="center" fullWidth maxWidth="sm">
                <Text weight="bold" align="center">
                    Additional Feedback
                </Text>
                <TextArea value={additionalFeedback || ''} onChange={setAdditionalFeedback} minRows={6} fullWidth />
            </Stack>

            <Stack fullWidth maxWidth="sm">
                <ProgramImpactSection
                    options={programImpact}
                    value={programImpact.filter((option) => option.value).map((option) => option.id)}
                    onChange={setProgramImpact}
                />
            </Stack>

            <Stack gap="sm" align="center">
                <PillButton onClick={submitReview} disabled={!isButtonEnabled}>
                    {pageState === PageStates.submitting ? 'Submitting…' : 'Submit'}
                </PillButton>
                {!!errorMessage && (
                    <Text tone="error" align="center">
                        {errorMessage}
                    </Text>
                )}
            </Stack>
        </MainContainer>
    );
};

export default ReviewProvider;
